import { Injectable } from '@angular/core';
import { Auth, User, onAuthStateChanged, createUserWithEmailAndPassword, updateProfile, signInWithEmailAndPassword, signOut, getAuth } from '@angular/fire/auth';
import { Observable, BehaviorSubject, from } from 'rxjs';
import { Firestore, collection, doc, setDoc, getDoc, getDocs, query, where, updateDoc, deleteDoc } from '@angular/fire/firestore';
import { Storage, ref, uploadBytes, getDownloadURL } from '@angular/fire/storage';
import { Router } from '@angular/router';
import { initializeApp, getApps, FirebaseApp, deleteApp } from '@angular/fire/app';
import { environment } from '../../../environments/environment';

export interface EditorUser {
  id: string;
  name: string;
  email: string;
  role: string; // 'editor', 'admin', 'general_editor'
  profileImage?: string;
  createdAt: Date;
  lastLogin?: Date;
  status: 'active' | 'inactive';
  deletedAt?: Date;
  city?: string; // Solo para editores de ciudad
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<EditorUser | null | undefined>(undefined);
  private userInitialized = false;
  private isRegistering = false; // Flag para evitar conflictos durante el registro
  currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private auth: Auth,
    private firestore: Firestore,
    private storage: Storage,
    private router: Router
  ) {
    // Verificar si hay un usuario al iniciar
    const currentUser = auth.currentUser;
    if (currentUser) {
      // console.log('Usuario encontrado al iniciar:', currentUser.uid);
      this.initializeUserData(currentUser);
    } else {
      this.userInitialized = true;
    }

    // Escuchar cambios en el estado de autenticación
    onAuthStateChanged(auth, (user) => {
      // console.log('Estado de autenticación cambió:', user?.uid);
      
      // Si estamos en proceso de registro, ignorar este evento para evitar conflictos
      if (this.isRegistering) {
        // console.log('Ignorando cambio de autenticación durante registro');
        return;
      }
      
      if (user) {
        this.initializeUserData(user);
      } else {
        // console.log('No hay usuario autenticado, emitiendo null');
        this.currentUserSubject.next(null);
        this.userInitialized = true;
      }
    });
  }

  private async initializeUserData(user: User) {
    try {
      // console.log('Inicializando datos de usuario:', user.uid);
      const userDoc = await getDoc(doc(this.firestore, 'editors', user.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data() as EditorUser;
        // console.log('Datos de usuario encontrados:', userData);
        
        const completeUserData: EditorUser = {
          ...userData,
          id: user.uid,
          lastLogin: new Date()
        };

        this.currentUserSubject.next(completeUserData);

        // Actualizar último login
        await updateDoc(doc(this.firestore, 'editors', user.uid), {
          lastLogin: new Date()
        });
      } else {
        // console.log('No se encontraron datos de usuario en Firestore');
        this.currentUserSubject.next(null);
      }
    } catch (error) {
      console.error('Error al inicializar datos de usuario:', error);
      this.currentUserSubject.next(null);
    } finally {
      this.userInitialized = true;
    }
  }

  /**
   * Espera hasta que el servicio de autenticación termine de inicializar
   * y Firebase Auth resuelva el estado de autenticación actual.
   */
  async waitForUserInitialization(): Promise<EditorUser | null | undefined> {
    // console.log('waitForUserInitialization - Iniciando espera...');
    
    // Si ya está inicializado, retornar el usuario actual
    if (this.userInitialized) {
      // console.log('waitForUserInitialization - Ya inicializado, usuario:', this.getCurrentUser()?.name || 'null');
      return this.getCurrentUser();
    }

    // Primera espera: esperar a que userInitialized sea true
    await new Promise<void>((resolve) => {
      const checkInterval = setInterval(() => {
        if (this.userInitialized) {
          clearInterval(checkInterval);
          // console.log('waitForUserInitialization - Inicialización básica completada');
          // Agregar un pequeño delay para asegurar que los datos estén listos
          setTimeout(resolve, 100);
        }
      }, 50);
    });
    
    // Segunda espera: esperar un poco más para dar tiempo a Firebase Auth
    await new Promise<void>(resolve => setTimeout(resolve, 500));
    
    // Verificar si hay un usuario autenticado
    const user = this.getCurrentUser();
    // console.log('waitForUserInitialization - Estado final, usuario:', user?.name || 'null');
    
    return user;
  }

  getCurrentUser(): EditorUser | null | undefined {
    return this.currentUserSubject.value;
  }

  async login(email: string, password: string): Promise<EditorUser> {
    try {
      // console.log('Intentando iniciar sesión:', email);
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      // console.log('Usuario autenticado:', userCredential.user.uid);
      
      const userDoc = await getDoc(doc(this.firestore, 'editors', userCredential.user.uid));
      
      if (!userDoc.exists()) {
        console.error('Usuario no encontrado en Firestore');
        throw new Error('Usuario no encontrado en la base de datos');
      }

      const userData = userDoc.data() as EditorUser;
      // console.log('Datos de usuario recuperados:', userData);
      
      if (userData.status === 'inactive') {
        console.error('Usuario inactivo');
        throw new Error('Usuario inactivo. Contacte al administrador.');
      }

      // Validar acceso según el dominio
      this.validateDomainAccess(userData);

      // Asegurarse de que el ID esté presente
      const completeUserData: EditorUser = {
        ...userData,
        id: userCredential.user.uid
      };

      // Actualizar el subject con los datos completos
      this.currentUserSubject.next(completeUserData);

      return completeUserData;
    } catch (error: any) {
      console.error('Error en login:', error);
      throw new Error(`Error al iniciar sesión: ${error.message}`);
    }
  }

  async createAdminUser(email: string, password: string): Promise<EditorUser> {
    try {
      // 1. Crear usuario en Authentication
      const userCredential = await createUserWithEmailAndPassword(
        this.auth,
        email,
        password
      );

      // 2. Crear documento en la colección 'editors' con rol de admin
      const adminUser: EditorUser = {
        id: userCredential.user.uid,
        name: 'Administrador',
        email: email,
        role: 'admin',
        createdAt: new Date(),
        lastLogin: new Date(),
        status: 'active'
      };

      await setDoc(doc(this.firestore, 'editors', userCredential.user.uid), adminUser);

      return adminUser;
    } catch (error: any) {
      throw new Error(`Error al crear usuario administrador: ${error.message}`);
    }
  }

  async registerEditor(editorData: {
    email: string;
    password: string;
    name: string;
    role?: string;
    profileImage?: File;
    city?: string;
  }): Promise<EditorUser> {
    try {
      // Verificar que hay un administrador autenticado
      const currentUser = this.auth.currentUser;
      const currentUserData = this.currentUserSubject.value;
      
      if (!currentUser || !currentUserData) {
        throw new Error('Debe estar autenticado como administrador para crear editores');
      }

      // Crear una instancia secundaria de Firebase para no afectar la sesión actual
      const secondaryApp = initializeApp(environment.firebase, `secondary-${Date.now()}`);
      const secondaryAuth = getAuth(secondaryApp);

      let profileImageUrl = '';
      let userCredential: any = null;
      
      try {
        // console.log('🚀 Iniciando registro de editor:', editorData.email);

        // 1. Crear usuario en la instancia secundaria de Authentication
        // console.log('📝 Creando usuario en instancia secundaria de Firebase Auth...');
        userCredential = await createUserWithEmailAndPassword(
          secondaryAuth,
          editorData.email,
          editorData.password
        );
        // console.log('✅ Usuario creado en Auth secundario:', userCredential.user.uid);
        
        // 2. Si hay imagen de perfil, subirla directamente a la ubicación final
        if (editorData.profileImage) {
          // console.log('📸 Subiendo imagen de perfil...');
          // Usar directamente el UID del usuario creado
          const imageRef = ref(this.storage, `editors/${userCredential.user.uid}/profile`);
          
          try {
            const uploadResult = await uploadBytes(imageRef, editorData.profileImage);
            profileImageUrl = await getDownloadURL(uploadResult.ref);
            // console.log('✅ Imagen subida:', profileImageUrl);
          } catch (uploadError) {
            console.error('❌ Error subiendo imagen:', uploadError);
            throw new Error('Error al subir la imagen de perfil');
          }
        }

        // 3. Actualizar el perfil del usuario con el nombre
        // console.log('👤 Actualizando perfil de usuario...');
        await updateProfile(userCredential.user, {
          displayName: editorData.name,
          photoURL: profileImageUrl
        });
        // console.log('✅ Perfil actualizado');

        // 4. Crear documento en la colección 'editors'
        // console.log('💾 Guardando documento en Firestore...');
        
        // Preparar el objeto del editor, excluyendo campos undefined
        const editorDataToSave: any = {
          id: userCredential.user.uid,
          name: editorData.name,
          email: editorData.email,
          role: editorData.role || 'editor',
          profileImage: profileImageUrl,
          createdAt: new Date(),
          lastLogin: new Date(),
          status: 'active'
        };

        // Solo agregar city si tiene un valor válido
        if (editorData.city && editorData.city.trim() !== '') {
          editorDataToSave.city = editorData.city;
        }

        // console.log('📄 Documento a guardar:', editorDataToSave);

        const docRef = doc(this.firestore, 'editors', userCredential.user.uid);
        await setDoc(docRef, editorDataToSave);
        
        // console.log('✅ Documento guardado exitosamente en Firestore');
        
        // Verificar que se guardó correctamente
        console.log('🔍 Verificando que el documento se guardó...');
        const savedDoc = await getDoc(docRef);
        if (savedDoc.exists()) {
          // console.log('✅ Verificación exitosa - documento existe:', savedDoc.data());
        } else {
          console.error('❌ ERROR: El documento no se encontró después de guardarlo');
          throw new Error('El documento no se guardó correctamente en Firestore');
        }

        // console.log('🎉 Editor registrado exitosamente:', editorDataToSave);
        return editorDataToSave as EditorUser;
      } finally {
        // 5. Cerrar sesión del usuario recién creado en la instancia secundaria
        if (secondaryAuth) {
          // console.log('🔓 Cerrando sesión del usuario en la instancia secundaria...');
          await signOut(secondaryAuth);
        }
        
        // 6. Eliminar la instancia secundaria para liberar recursos
        if (secondaryApp) {
          // console.log('🧹 Eliminando instancia secundaria de Firebase...');
          await deleteApp(secondaryApp);
        }
      }
    } catch (error: any) {
      console.error('❌ Error en registerEditor:', error);
      throw new Error(`Error al registrar editor: ${error.message}`);
    }
  }

  async getAllEditors(): Promise<EditorUser[]> {
    const editorsRef = collection(this.firestore, 'editors');
    const querySnapshot = await getDocs(editorsRef);
    return querySnapshot.docs.map(doc => ({
      ...doc.data() as EditorUser,
      id: doc.id
    }));
  }

  async updateEditorStatus(editorId: string, status: 'active' | 'inactive'): Promise<void> {
    await updateDoc(doc(this.firestore, 'editors', editorId), { status });
  }

  async updateEditorProfile(editorId: string, data: Partial<EditorUser>): Promise<void> {
    await updateDoc(doc(this.firestore, 'editors', editorId), data);
  }

  async updateEditor(editorId: string, data: Partial<Omit<EditorUser, 'profileImage'>> & { profileImage?: File }) {
    try {
      const editorRef = doc(this.firestore, 'editors', editorId);
      const updateData: { [key: string]: any } = { ...data };

      // Manejar imagen de perfil si se proporciona
      if (data.profileImage) {
        const imageRef = ref(this.storage, `editors/${editorId}/profile`);
        const uploadResult = await uploadBytes(imageRef, data.profileImage);
        updateData['profileImage'] = await getDownloadURL(uploadResult.ref);
      }

      // Eliminar campos undefined para evitar errores de Firestore
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      // Eliminar el campo profileImage del objeto data ya que se maneja por separado
      delete updateData['profileImage'];

      // console.log('📝 Datos a actualizar:', updateData);
      await updateDoc(editorRef, updateData);

      // Actualizar el estado local si el editor actualizado es el usuario actual
      if (this.currentUserSubject.value?.id === editorId) {
        const updatedUser = { ...this.currentUserSubject.value, ...updateData };
        this.currentUserSubject.next(updatedUser as EditorUser);
      }
    } catch (error: any) {
      throw new Error(`Error al actualizar editor: ${error.message}`);
    }
  }

  async deleteEditor(editorId: string): Promise<void> {
    try {
      const editorRef = doc(this.firestore, 'editors', editorId);
      
      // Eliminar físicamente el documento
      await deleteDoc(editorRef);
      // console.log('Editor eliminado exitosamente:', editorId);
    } catch (error: any) {
      console.error('Error al eliminar editor:', error);
      throw new Error(`Error al eliminar editor: ${error.message}`);
    }
  }

  async logout() {
    try {
      await signOut(this.auth);
      this.currentUserSubject.next(null); // Emitir null al cerrar sesión
      this.router.navigate(['/login']);
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  }

  /**
   * Limpia imágenes temporales que puedan haber quedado
   */
  private async cleanupTempImages(tempImageId: string): Promise<void> {
    try {
      const tempImageRef = ref(this.storage, `editors/temp/${tempImageId}/profile`);
      // Aquí podríamos implementar la eliminación de la imagen temporal
      // pero por simplicidad, las dejamos que se limpien por un proceso de mantenimiento
      // console.log('Cleanup de imagen temporal programado:', tempImageId);
    } catch (error) {
      // console.warn('No se pudo limpiar imagen temporal:', error);
    }
  }

  /**
   * Validar el acceso del usuario basado en el dominio actual y su rol.
   * Lanza un error si el acceso es denegado.
   */
  private validateDomainAccess(userData: EditorUser): void {
    const currentHostname = window.location.hostname;
    const currentPath = window.location.pathname;
    
    // Verificar que el usuario existe en nuestra base de datos
    if (!userData || !userData.id) {
      throw new Error('Usuario no autorizado para acceder al sistema.');
    }

    // Verificar que el usuario esté activo
    if (userData.status !== 'active') {
      throw new Error('Usuario inactivo. Contacte al administrador.');
    }
    
    // Si es el dominio principal (borde.news), permite administradores y editores generales
    if (currentHostname === 'borde.news') {
      if (!['admin', 'general_editor'].includes(userData.role)) {
        throw new Error('Acceso denegado. Solo administradores y editores generales pueden acceder a este portal.');
      }
    }
    
    // Si está intentando acceder a rutas de administración específicas, solo permite administradores
    if (currentPath.includes('/admin/editores') || currentPath.includes('/admin/crear-admin') || currentPath.includes('/editor-management')) {
      if (userData.role !== 'admin') {
        throw new Error('Acceso denegado. Solo administradores pueden acceder a esta sección.');
      }
    }
    
    // Para subdominios y otras rutas, permite editores de ciudad, editores generales y admins
    if (!['editor', 'admin', 'general_editor'].includes(userData.role)) {
      throw new Error('Acceso denegado. Solo personal autorizado puede acceder.');
    }
  }

  public isAccessAllowedForCurrentDomain(userRole: string): boolean {
    const currentHostname = window.location.hostname;
    
    // Si es el dominio principal (borde.news), permite administradores y editores generales
    if (currentHostname === 'borde.news') {
      return ['admin', 'general_editor'].includes(userRole);
    }
    
    // Para subdominios, permite editores de ciudad, editores generales y admins
    return ['editor', 'admin', 'general_editor'].includes(userRole);
  }

  public getCurrentDomain(): string {
    return window.location.hostname;
  }

  public getDomainInfo(): { domain: string, isMainDomain: boolean, allowedRoles: string[] } {
    const currentHostname = window.location.hostname;
    
    if (currentHostname === 'borde.news') {
      return {
        domain: currentHostname,
        isMainDomain: true,
        allowedRoles: ['admin', 'general_editor']
      };
    }
    
    return {
      domain: currentHostname,
      isMainDomain: false,
      allowedRoles: ['editor', 'admin', 'general_editor']
    };
  }
}
